import React, { useState, useEffect, useContext, useRef } from 'react'
import { Box, Button, Typography, TextField } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'

// Components
import { CustomModal } from '../../common/CustomModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import EntrySection from '../editBill/EntrySection'
import { CardBox, CardBoxTopBar } from '../../../theme/styleComponents'

// Others
import {
  EntryType,
  useGenerateQuickBillMutation,
  useFetchAllFeeTypesLazyQuery,
  BillingType,
  FeeType,
  FetchAllFeeTypesQuery,
  GenerateQuickBillMutation,
  UserType
} from '../../../../generated'
import { GRAPHQL_QUERY_POLICY, MONTHLY_FLAT_FEE_TYPE } from '../../../constants'
import { Alert } from '../../common/Alert'
import { EditBillForm, EditBillEntryForm } from '../../../interfaceTypes/forms'
import { editBillEntryValidationSchema } from '../../../validationSchema'
import { formatQuickBillMutationData, calculateNetTotal } from '../../../utils/billing'
import { AuthContext } from '../../../context'
import { getBillableStatus } from '../../../utils/entry'
import { generateUniqueId } from '../../../utils'

interface GenerateMonthlyFlatFeeBillModalProps {
  isOpen: boolean
  onClose: () => void
  caseId: string
  caseUsers?: any[]
  billingType?: BillingType | null
  refetch?: () => void
  disableNavigateOnSuccess?: boolean
}

const GenerateMonthlyFlatFeeBillModal: React.FC<GenerateMonthlyFlatFeeBillModalProps> = ({
  isOpen,
  onClose,
  caseId,
  caseUsers,
  billingType,
  refetch,
  disableNavigateOnSuccess = false
}) => {
  const navigate = useNavigate()
  const authContext = useContext(AuthContext)
  const currentUser = authContext?.currentUser
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [monthlyFlatFeeType, setMonthlyFlatFeeType] = useState<FeeType | null>(null)
  const [fetchedFeeTypes, setFetchedFeeTypes] = useState<FeeType[]>([])
  const hasFetchedFeeTypes = useRef(false)

  const methods = useForm<EditBillForm & { billingNotes: string }>({
    resolver: yupResolver(editBillEntryValidationSchema),
    mode: 'onChange',
    defaultValues: {
      timeEntries: [],
      legalFeeEntries: [],
      expenseEntries: [],
      investigatorEntries: [],
      discountPercentage: null,
      billingNotes: ''
    }
  })

  const { reset, watch, setValue } = methods
  const legalFeeEntries = watch('legalFeeEntries')
  const billingNotes = watch('billingNotes')
  const { netTotal } = calculateNetTotal(watch())

  const [fetchAllFeeTypes] = useFetchAllFeeTypesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: (data: FetchAllFeeTypesQuery) => {
      if (data?.fetchAllFeeTypes?.feeTypes) {
        const feeTypes = data.fetchAllFeeTypes.feeTypes as FeeType[]
        setFetchedFeeTypes(feeTypes)
        const monthlyFeeType = feeTypes.find(
          (feeType): feeType is FeeType => feeType?.feeType === MONTHLY_FLAT_FEE_TYPE
        )
        if (monthlyFeeType) {
          setMonthlyFlatFeeType(monthlyFeeType)
        }
      }
    },
    onError: (error: Error) => {
      Alert.error(error.message || 'Failed to fetch fee types')
    }
  })

  // Pre-populate the legal fee entry when modal opens and fee type is found
  useEffect(() => {
    if (isOpen && monthlyFlatFeeType && currentUser && billingType) {
      const currentEntries = watch('legalFeeEntries')

      // Find Responsible Attorney 1 from caseUsers, or fall back to current user
      const responsibleAttorney1CaseUser = caseUsers?.find(
        (caseUser: any) => caseUser?.userType === UserType.ResponsibleAttorney_1
      )

      const selectedUserId =
        responsibleAttorney1CaseUser?.user?.id || responsibleAttorney1CaseUser?.userId || currentUser.id || ''

      if (currentEntries.length === 0) {
        const billable = getBillableStatus({
          billingType,
          isFlatFeeHoursExhausted: false,
          entryType: EntryType.Legal
        })
        const newEntry: EditBillEntryForm = {
          date: new Date(),
          entryType: EntryType.Legal,
          userId: selectedUserId,
          description: null,
          billable,
          showOnBill: true,
          isNew: true,
          duration: null,
          rate: 0,
          amount: null,
          feeTypeId: monthlyFlatFeeType.id,
          categoryId: null,
          caseId,
          creatorId: currentUser.id,
          id: generateUniqueId()
        }
        setValue('legalFeeEntries', [newEntry])
      } else if (currentEntries.length > 0) {
        // Update existing entry with Monthly Flat Fee and selected user
        setValue('legalFeeEntries.0.feeTypeId', monthlyFlatFeeType.id)
        setValue('legalFeeEntries.0.userId', selectedUserId)
      }
    }
  }, [isOpen, monthlyFlatFeeType, currentUser, billingType, caseId, caseUsers, setValue, watch])

  const [generateQuickBillMutation, { loading: generatingBill }] = useGenerateQuickBillMutation({
    onError: (error: Error) => {
      Alert.error(error.message || 'Failed to generate bill')
    },
    onCompleted: (data: GenerateQuickBillMutation) => {
      if (data) {
        const {
          generateQuickBill: { response, billId }
        } = data

        if (response?.status === 200) {
          Alert.success('Bill generated successfully!')
          setShowConfirmation(false)
          onClose()
          if (refetch) {
            refetch()
          }
          if (!disableNavigateOnSuccess) {
            navigate(`/billing/bill-detail/${billId}`)
          }
        } else {
          Alert.error('Failed to generate bill')
        }
      }
    }
  })

  useEffect(() => {
    if (isOpen && caseId && !hasFetchedFeeTypes.current) {
      hasFetchedFeeTypes.current = true
      fetchAllFeeTypes({
        variables: {
          fetchFeeTypes: {
            keyword: '', // Empty keyword to fetch all fee types
            paginationOptions: {
              limit: 200, // Increased limit to ensure we get all fee types including Monthly Flat Fee
              page: 1
            }
          }
        }
      })
    } else if (!isOpen) {
      // Reset form and flag when modal closes
      hasFetchedFeeTypes.current = false
      reset({
        timeEntries: [],
        legalFeeEntries: [],
        expenseEntries: [],
        investigatorEntries: [],
        discountPercentage: null,
        billingNotes: ''
      })
      setShowConfirmation(false)
      setMonthlyFlatFeeType(null)
      setFetchedFeeTypes([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, caseId])

  const handleConfirmGenerate = () => {
    setShowConfirmation(true)
  }

  const handleCancelConfirm = () => {
    setShowConfirmation(false)
  }

  const handleFinalConfirm = async () => {
    const data = watch()
    if (!caseId) return

    // Prepare billing notes array
    const billingNotesArray: string[] = []

    // Add billing notes from the separate field
    if (data.billingNotes?.trim()) {
      billingNotesArray.push(data.billingNotes.trim())
    }

    // Add billing notes from entries with sendNotesToMatterNotes flag
    const allEntries = [
      ...data.timeEntries,
      ...data.legalFeeEntries,
      ...data.expenseEntries,
      ...data.investigatorEntries
    ]
    allEntries.forEach(entry => {
      if (entry.sendNotesToMatterNotes && entry.description) {
        billingNotesArray.push(entry.description)
      }
    })

    const formattedData = formatQuickBillMutationData({
      caseId,
      data: {
        ...data,
        discountPercentage: null // No discount for Monthly Flat Fee
      },
      totalAmount: netTotal,
      discount: 0
    })

    // Override billingNotes with our combined array
    formattedData.billingNotes = billingNotesArray

    await generateQuickBillMutation({
      variables: {
        generateQuickBillInput: {
          ...formattedData,
          isFinalBill: true // Always generate final bill for Monthly Flat Fee
        }
      }
    })
  }

  return (
    <>
      <CustomModal title="Generate Bill for Monthly Flat Fee" isOpen={isOpen} handleClose={onClose} maxWidth="xl">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormProvider {...methods}>
            <Box sx={{ p: { xs: 0, sm: 2 } }}>
              <EntrySection
                key="legalFeeEntries"
                title="Legal fee"
                fieldName="legalFeeEntries"
                entryType={EntryType.Legal}
                caseId={caseId}
                caseUsers={caseUsers}
                billingType={billingType || undefined}
                isFlatFeeHoursExhausted={false}
                initialFeeTypes={fetchedFeeTypes}
                skipInitialFeeTypeFetch={fetchedFeeTypes.length > 0}
              />

              <CardBox mb={3}>
                <CardBoxTopBar>
                  <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
                    Billing Notes
                  </Typography>
                </CardBoxTopBar>
                <Box sx={{ p: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Billing Notes"
                    value={billingNotes || ''}
                    onChange={e => setValue('billingNotes', e.target.value)}
                    placeholder="Enter billing notes..."
                  />
                </Box>
              </CardBox>

              <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={2} mt={3}>
                <Button variant="outlined" onClick={onClose} disabled={generatingBill}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmGenerate}
                  disabled={generatingBill || !legalFeeEntries?.length || !legalFeeEntries[0]?.amount}
                >
                  Generate Final Bill
                </Button>
              </Box>
            </Box>
          </FormProvider>
        </LocalizationProvider>
      </CustomModal>

      <ConfirmationModal
        isOpen={showConfirmation}
        title="Generate Final Bill"
        message="Are you sure you want to generate a final bill for Monthly Flat Fee? This action cannot be undone."
        confirmText="Generate Bill"
        cancelText="Cancel"
        variant="danger"
        isLoading={generatingBill}
        onConfirm={handleFinalConfirm}
        onCancel={handleCancelConfirm}
      />
    </>
  )
}

export default GenerateMonthlyFlatFeeBillModal
